import { api } from './api';

export interface ParishBookCategoryDto {
    id: string;
    entityId: string;
    name: string;
}

export interface ParishBookPersonDto {
    id: string;
    entityId: string;
    categoryId: string;
    firstName: string;
    lastName: string;
    availability?: string;
    description?: string;
    phoneNumber?: string;
    email?: string;
    visibleForGuests: boolean;
}

export interface CreateParishBookCategoryDto {
    name: string;
}

export interface UpdateParishBookCategoryDto {
    id: string;
    name: string;
}

export interface CreateParishBookPersonDto {
    categoryId: string;
    firstName: string;
    lastName: string;
    availability?: string;
    description?: string;
    phoneNumber?: string;
    email?: string;
    visibleForGuests?: boolean;
}

export interface UpdateParishBookPersonDto {
    id: string;
    categoryId: string;
    firstName: string;
    lastName: string;
    availability?: string;
    description?: string;
    phoneNumber?: string;
    email?: string;
    visibleForGuests?: boolean;
}

export const parishBookApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getParishBookCategories: builder.query<ParishBookCategoryDto[], string>({
            query: (entityId) => `entity/${entityId}/parish-book/categories`,
            providesTags: ['ParishBookCategory'],
        }),
        getParishBookCategory: builder.query<ParishBookCategoryDto, { entityId: string; id: string }>({
            query: ({ entityId, id }) => `entity/${entityId}/parish-book/categories/${id}`,
            providesTags: ['ParishBookCategory'],
        }),
        createParishBookCategory: builder.mutation<ParishBookCategoryDto, { entityId: string; data: CreateParishBookCategoryDto }>({
            query: ({ entityId, data }) => ({
                url: `entity/${entityId}/parish-book/categories`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ParishBookCategory'],
        }),
        updateParishBookCategory: builder.mutation<void, { entityId: string; id: string; data: UpdateParishBookCategoryDto }>({
            query: ({ entityId, id, data }) => ({
                url: `entity/${entityId}/parish-book/categories/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['ParishBookCategory'],
        }),
        deleteParishBookCategory: builder.mutation<void, { entityId: string; id: string }>({
            query: ({ entityId, id }) => ({
                url: `entity/${entityId}/parish-book/categories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ParishBookCategory', 'ParishBookPerson'],
        }),
        getParishBookPersons: builder.query<ParishBookPersonDto[], string>({
            query: (entityId) => `entity/${entityId}/parish-book/persons`,
            providesTags: ['ParishBookPerson'],
        }),
        getParishBookPersonsByCategory: builder.query<ParishBookPersonDto[], { entityId: string; categoryId: string }>({
            query: ({ entityId, categoryId }) => `entity/${entityId}/parish-book/categories/${categoryId}/persons`,
            providesTags: ['ParishBookPerson'],
        }),
        getParishBookPerson: builder.query<ParishBookPersonDto, { entityId: string; id: string }>({
            query: ({ entityId, id }) => `entity/${entityId}/parish-book/persons/${id}`,
            providesTags: ['ParishBookPerson'],
        }),
        createParishBookPerson: builder.mutation<ParishBookPersonDto, { entityId: string; data: CreateParishBookPersonDto }>({
            query: ({ entityId, data }) => ({
                url: `entity/${entityId}/parish-book/persons`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ParishBookPerson'],
        }),
        updateParishBookPerson: builder.mutation<void, { entityId: string; id: string; data: UpdateParishBookPersonDto }>({
            query: ({ entityId, id, data }) => ({
                url: `entity/${entityId}/parish-book/persons/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['ParishBookPerson'],
        }),
        deleteParishBookPerson: builder.mutation<void, { entityId: string; id: string }>({
            query: ({ entityId, id }) => ({
                url: `entity/${entityId}/parish-book/persons/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ParishBookPerson'],
        }),
    }),
});

export const {
    useGetParishBookCategoriesQuery,
    useGetParishBookCategoryQuery,
    useCreateParishBookCategoryMutation,
    useUpdateParishBookCategoryMutation,
    useDeleteParishBookCategoryMutation,
    useGetParishBookPersonsQuery,
    useGetParishBookPersonsByCategoryQuery,
    useGetParishBookPersonQuery,
    useCreateParishBookPersonMutation,
    useUpdateParishBookPersonMutation,
    useDeleteParishBookPersonMutation,
} = parishBookApi;
